import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ArrowLeft, Save, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { useGamesStore, useUserStore } from '../stores/StoreProvider';
import OrganizationAutocomplete from '../components/OrganizationAutocomplete';

const AddGame = observer(() => {
  const navigate = useNavigate();
  const gamesStore = useGamesStore();
  const userStore = useUserStore();

  const formatYMD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [ourScore, setOurScore] = useState('');
  const [theirScore, setTheirScore] = useState('');
  const [goals, setGoals] = useState('');
  const [assists, setAssists] = useState('');
  // Goalie stats
  const [startedGame, setStartedGame] = useState(true);
  const [minutesPlayed, setMinutesPlayed] = useState('');
  const [shotsAgainst, setShotsAgainst] = useState('');
  const [saves, setSaves] = useState('');
  const [goalsAgainst, setGoalsAgainst] = useState('');
  const [powerplayGoalsAgainst, setPowerplayGoalsAgainst] = useState('0');
  const [shorthandedGoalsAgainst, setShorthandedGoalsAgainst] = useState('0');
  const [gameDate, setGameDate] = useState(new Date());
  const [dateInput, setDateInput] = useState(formatYMD(new Date()));
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const handleDateInputChange = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format with slashes
    let formatted = '';
    if (digits.length >= 1) formatted += digits.slice(0, 2);
    if (digits.length >= 3) formatted += '/' + digits.slice(2, 4);
    if (digits.length >= 5) formatted += '/' + digits.slice(4, 8);
    
    setDateInput(formatted);
    
    // Try to parse the date if we have enough digits
    if (digits.length === 8) {
      const month = parseInt(digits.slice(0, 2)) - 1; // JS months are 0-indexed
      const day = parseInt(digits.slice(2, 4));
      const year = parseInt(digits.slice(4, 8));
      
      if (month >= 0 && month <= 11 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
        const newDate = new Date(year, month, day);
        if (newDate.getMonth() === month && newDate.getDate() === day && newDate.getFullYear() === year) {
          setGameDate(newDate);
        }
      }
    }
  };

  const handleDateInputBlur = () => {
    // Validate and format the date on blur
    const digits = dateInput.replace(/\D/g, '');
    if (digits.length === 8) {
      const month = parseInt(digits.slice(0, 2)) - 1;
      const day = parseInt(digits.slice(2, 4));
      const year = parseInt(digits.slice(4, 8));
      
      const newDate = new Date(year, month, day);
      if (newDate.getMonth() === month && newDate.getDate() === day && newDate.getFullYear() === year) {
        setGameDate(newDate);
        setDateInput(formatYMD(newDate));
      } else {
        // Invalid date, reset to current date
        setDateInput(formatYMD(gameDate));
      }
    } else {
      // Incomplete date, reset to current date
      setDateInput(formatYMD(gameDate));
    }
  };

  const handleSaveGame = async () => {
    // Validate inputs
    if (!selectedOrganization) {
      toast.error('Please select an opponent.');
      return;
    }
    if (!ourScore.trim() || !theirScore.trim()) {
      toast.error('Please enter both scores.');
      return;
    }
    if (isNaN(parseInt(ourScore)) || isNaN(parseInt(theirScore))) {
      toast.error('Please enter valid scores.');
      return;
    }
    // Validate based on position
    if (userStore.profile?.position === 'goalie') {
      if (!shotsAgainst.trim() || !saves.trim() || !goalsAgainst.trim()) {
        toast.error('Please enter shots against, saves, and goals against.');
        return;
      }
      if (isNaN(parseInt(shotsAgainst)) || isNaN(parseInt(saves)) || isNaN(parseInt(goalsAgainst))) {
        toast.error('Please enter valid numbers for goalie stats.');
        return;
      }
    } else {
      if (!goals.trim() || !assists.trim()) {
        toast.error('Please enter your goals and assists.');
        return;
      }
      if (isNaN(parseInt(goals)) || isNaN(parseInt(assists))) {
        toast.error('Please enter valid numbers for goals and assists.');
        return;
      }
    }

    try {
      const gameData = {
        organization_id: selectedOrganization.id,
        our_score: parseInt(ourScore),
        their_score: parseInt(theirScore),
        goals: userStore.profile?.position === 'goalie' ? 0 : parseInt(goals),
        assists: userStore.profile?.position === 'goalie' ? 0 : parseInt(assists),
        date: gameDate.toISOString().split('T')[0], // Selected date
      };

      // Add goalie stats if user is a goalie
      if (userStore.profile?.position === 'goalie') {
        gameData.started_game = startedGame;
        gameData.minutes_played = minutesPlayed ? parseInt(minutesPlayed) : null;
        gameData.shots_against = parseInt(shotsAgainst);
        gameData.saves = parseInt(saves);
        gameData.goals_against = parseInt(goalsAgainst);
        gameData.powerplay_goals_against = parseInt(powerplayGoalsAgainst);
        gameData.shorthanded_goals_against = parseInt(shorthandedGoalsAgainst);
      }

      console.log('Saving game:', gameData);

      await gamesStore.createGame(gameData);

      toast.success('Game saved successfully!');
      navigate('/app/games');
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error(error.message || 'Failed to save game. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/app/games')}
            className="mr-4 p-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Add Game</h1>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Organization Selection */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Opponent</h2>
              <OrganizationAutocomplete
                onSelect={setSelectedOrganization}
                placeholder="Search for your opponent"
              />
              {selectedOrganization && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {selectedOrganization.image_url && (
                      <img
                        src={selectedOrganization.image_url}
                        alt="Team"
                        className="w-6 h-6 rounded object-contain"
                      />
                    )}
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Selected: {selectedOrganization.name}
                      </p>
                      {selectedOrganization.team_name && (
                        <p className="text-xs text-blue-700">
                          {selectedOrganization.team_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Game Date */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Game Date</h2>
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={dateInput}
                    onChange={(e) => handleDateInputChange(e.target.value)}
                    onBlur={handleDateInputBlur}
                    placeholder="MM/DD/YYYY"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={10}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setDatePickerOpen(true)}
                  className="px-3 py-3 border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <Calendar className="text-gray-400" size={16} />
                </button>
              </div>
            </div>

            {/* Game Score */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Game Score</h2>
              <div className="flex items-center justify-between space-x-4">
                <div className="flex-1 text-center">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Our Score</label>
                  <input
                    type="number"
                    value={ourScore}
                    onChange={(e) => setOurScore(e.target.value)}
                    placeholder="0"
                    min="0"
                    max="999"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="text-center px-4">
                  <p className="text-lg font-bold text-gray-500">vs</p>
                </div>
                <div className="flex-1 text-center">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Their Score</label>
                  <input
                    type="number"
                    value={theirScore}
                    onChange={(e) => setTheirScore(e.target.value)}
                    placeholder="0"
                    min="0"
                    max="999"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Personal Stats */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Performance</h2>
              {userStore.profile?.position === 'goalie' ? (
                // Goalie Stats
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Started Game</label>
                      <button
                        type="button"
                        onClick={() => setStartedGame(!startedGame)}
                        className={`w-full px-3 py-3 rounded-lg border-2 text-center font-medium transition-colors ${
                          startedGame
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {startedGame ? 'Yes' : 'No'}
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Minutes Played</label>
                      <input
                        type="number"
                        value={minutesPlayed}
                        onChange={(e) => setMinutesPlayed(e.target.value)}
                        placeholder="60"
                        min="0"
                        max="999"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Shots Against</label>
                      <input
                        type="number"
                        value={shotsAgainst}
                        onChange={(e) => setShotsAgainst(e.target.value)}
                        placeholder="0"
                        min="0"
                        max="999"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Saves</label>
                      <input
                        type="number"
                        value={saves}
                        onChange={(e) => setSaves(e.target.value)}
                        placeholder="0"
                        min="0"
                        max="999"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Goals Against</label>
                      <input
                        type="number"
                        value={goalsAgainst}
                        onChange={(e) => setGoalsAgainst(e.target.value)}
                        placeholder="0"
                        min="0"
                        max="99"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PP Goals Against</label>
                      <input
                        type="number"
                        value={powerplayGoalsAgainst}
                        onChange={(e) => setPowerplayGoalsAgainst(e.target.value)}
                        placeholder="0"
                        min="0"
                        max="99"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SH Goals Against</label>
                      <input
                        type="number"
                        value={shorthandedGoalsAgainst}
                        onChange={(e) => setShorthandedGoalsAgainst(e.target.value)}
                        placeholder="0"
                        min="0"
                        max="99"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
              ) : (
                // Skater Stats
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 text-center">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Goals</label>
                    <input
                      type="number"
                      value={goals}
                      onChange={(e) => setGoals(e.target.value)}
                      placeholder="0"
                      min="0"
                      max="99"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex-1 text-center">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assists</label>
                    <input
                      type="number"
                      value={assists}
                      onChange={(e) => setAssists(e.target.value)}
                      placeholder="0"
                      min="0"
                      max="99"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveGame}
              disabled={gamesStore.loading}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors ${
                gamesStore.loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              } flex items-center justify-center`}
            >
              {gamesStore.loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="mr-2" size={20} />
              )}
              {gamesStore.loading ? 'Saving...' : 'Save Game'}
            </button>
          </div>
        </div>

        {/* Date Picker Modal */}
        {datePickerOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Select Game Date</h3>
              <input
                type="date"
                value={gameDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  setGameDate(new Date(e.target.value));
                  setDatePickerOpen(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setDatePickerOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default AddGame; 